import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { feedbackAPI } from '../services/api';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import toast from 'react-hot-toast';

interface FeedbackButtonsProps {
  emailId: string;
  size?: 'sm' | 'default';
  className?: string;
}

export default function FeedbackButtons({ emailId, size = 'sm', className = '' }: FeedbackButtonsProps) {
  const queryClient = useQueryClient();

  // Get current feedback for this email
  const { data: feedback } = useQuery({
    queryKey: ['feedback', emailId],
    queryFn: async () => {
      const response = await feedbackAPI.get(emailId);
      return response.data;
    },
  });

  // Submit feedback mutation
  const submitFeedback = useMutation({
    mutationFn: ({ feedbackType }: { feedbackType: 'upvote' | 'downvote' }) =>
      feedbackAPI.submit(emailId, feedbackType),
    onSuccess: (_, variables) => {
      toast.success(`${variables.feedbackType === 'upvote' ? 'Upvoted' : 'Downvoted'} response`);
      queryClient.invalidateQueries({ queryKey: ['feedback', emailId] });
      queryClient.invalidateQueries({ queryKey: ['feedback', 'stats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    },
  });

  // Remove feedback mutation
  const removeFeedback = useMutation({
    mutationFn: () => feedbackAPI.remove(emailId),
    onSuccess: () => {
      toast.success('Feedback removed');
      queryClient.invalidateQueries({ queryKey: ['feedback', emailId] });
      queryClient.invalidateQueries({ queryKey: ['feedback', 'stats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to remove feedback');
    },
  });

  const handleFeedback = (feedbackType: 'upvote' | 'downvote') => {
    if (feedback?.feedback_type === feedbackType) {
      // Same feedback type clicked - remove it
      removeFeedback.mutate();
    } else {
      // Different feedback type or no feedback - submit it
      submitFeedback.mutate({ feedbackType });
    }
  };

  const currentFeedback = feedback?.feedback_type;
  const isLoading = submitFeedback.isPending || removeFeedback.isPending;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Button
        size={size}
        variant={currentFeedback === 'upvote' ? 'default' : 'outline'}
        onClick={() => handleFeedback('upvote')}
        disabled={isLoading}
        className={`
          ${currentFeedback === 'upvote' 
            ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' 
            : 'border-green-200 text-green-700 hover:bg-green-50'
          }
          ${size === 'sm' ? 'h-7 px-2' : ''}
        `}
      >
        <ThumbsUp className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />
      </Button>
      
      <Button
        size={size}
        variant={currentFeedback === 'downvote' ? 'default' : 'outline'}
        onClick={() => handleFeedback('downvote')}
        disabled={isLoading}
        className={`
          ${currentFeedback === 'downvote' 
            ? 'bg-red-600 hover:bg-red-700 text-white border-red-600' 
            : 'border-red-200 text-red-700 hover:bg-red-50'
          }
          ${size === 'sm' ? 'h-7 px-2' : ''}
        `}
      >
        <ThumbsDown className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />
      </Button>
    </div>
  );
}
